import { Form } from '@form/Form';
import FormInput from '@form/FormInput';
import { InFormLogin } from '@form/InFormLogin';
import { useApi } from '@hooks/useApi';
import useAuth from '@hooks/useAuth';
import { Button, Progress } from '@ui';
import { CompletedAnimation } from 'components/Animations/CompletedAnimation';
import AppLogin from 'components/auth/AppLogin';
import useTempAuth from 'components/auth/TempAuthProvider/useTempAuth';
import MollieConnectMandates from 'components/MollieConnect/MollieConnectMandates';
import { PaymentMethodSelection } from 'components/MollieConnect/PaymentMethods';
import Joi from 'joi';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useSettings } from '../../Clubs/hooks';
import Confirmation from '../../Confirmation';
import FormSteps, { FormStepsRef, Step, StepState } from '../../Form/utilities/FormSteps';
import FormFields from '../../Formular/FormFields';
import useFetchFields from '../../Formular/hooks/useFetchFields';
import Loader from '../../Loader';
import { usePaymentHandler } from '../../MollieConnect/hooks';
import VoucherFields, { VoucherCards } from '../../Vouchers/VoucherFields';
import { Addon, Allocation, Space } from '../types';
import type { ExtendedSlot } from '../Views/TableView/types';
import AddonSelection from './AddonSelection';
import BookersFieldArray from './BookersFieldArray';
import BookingRail from './BookingRail';
import BookingTrainerSelection from './BookingTrainerSelection';
import MollieConnectFeeWarning from '../../MollieConnect/MollieConnectFeeWarning';
import LengthInput from './LengthInput';

// how long the kiosk keeps the confetti screen up before handing over to the undo bar
const CONFIRMATION_AUTO_CLOSE_MS = 3000;

// short-lived permission to erase a booking again, handed out by the backend for kiosk
// bookings when the club enabled space_manager.kiosk.booking_undo_enabled
export interface BookingUndoGrant {
    token: string;
    until: string;
    seconds: number;
}

interface Props {
    slot: ExtendedSlot;
    callback?: (allocation: Allocation) => any;
    // fires immediately when booking/payment succeeds, before internal Confirmation renders
    onSuccess?: (allocation: Allocation, undo?: BookingUndoGrant) => void;
    clubSlug?: string;
    kioskMode?: boolean;
    bookingEndpoint?: string;
    summaryEndpoint?: string;
    submitText?: string;
    requestData?: Record<string, any>;
    showRuleChecker?: boolean;
    checkEndpoint?: string;
    /** the slot was fixed by the club (booking link): time and trainer are shown, not picked */
    fixedSlot?: { trainer?: { id: number; name: string } | null };
}

// Main booking assistant component that handles form steps for space/court reservations
const BookingAssistant = ({
    slot,
    callback,
    onSuccess,
    clubSlug,
    kioskMode,
    bookingEndpoint,
    summaryEndpoint,
    submitText,
    requestData,
    showRuleChecker = true,
    checkEndpoint,
    fixedSlot
}: Props) => {
    const api = useApi();
    const { t } = useTranslation('space-manager');
    const { isAuthenticated, user } = useAuth();
    const { data: spaceData, isLoading: isLoadingSpace } = useSWR<{
        data: Space;
    }>(`space-manager/space/${slot.spaceId}`);
    const { data: addonData, isLoading: isLoadingAddons } = useSWR<{ data: Addon[] }>(
        clubSlug ? `club/${clubSlug}/space-manager/addon` : null
    );
    const { isLoading: isLoadingFields, fields, validationSchema } = useFetchFields(spaceData?.data.form_id);
    const [isLoading, setIsLoading] = useState(false);

    const [userFound, setUserFound] = useState(!!user);
    const [bookingData, setBookingData] = useState(null);
    // set when the backend granted an undo window for this booking; the confirmation then
    // closes itself so the undo bar behind the modal is actually reachable
    const [undoGrant, setUndoGrant] = useState<BookingUndoGrant | null>(null);
    const [hideBookers, { isLoading: isLoadingSettings }] = useSettings('space_manager.booking.hide_bookers', clubSlug);
    const { redirectToCheckout, preparePopup, handleError, currentPayment } = usePaymentHandler();
    // set by the rule check in the rail when the RequiresApproval effect applies — the booking
    // then becomes a request: nothing is charged now, so no checkout popup must be opened
    const [requiresApproval, setRequiresApproval] = useState(false);
    // mirrors the wizard's position so the rail's button can say "Weiter" or the order label
    const [stepState, setStepState] = useState<StepState | null>(null);
    // the OnlinePayment rule effect can switch online payment per booking and pin its method
    const [onlinePaymentsRule, setOnlinePaymentsRule] = useState<boolean | undefined>();
    const [forcedPaymentMethod, setForcedPaymentMethod] = useState<string | undefined>();
    const handlePaymentRuleChange = useCallback((onlinePayments?: boolean, paymentMethod?: string) => {
        setOnlinePaymentsRule(onlinePayments);
        setForcedPaymentMethod(paymentMethod);
    }, []);
    const hasOnlinePayments = (onlinePaymentsRule ?? Boolean(spaceData?.data.online_payments)) && !requiresApproval;
    // the payment status is polled, so a paid checkout can take a moment to confirm; keep the button
    // busy until then or a second click books the slot twice. A failed payment releases it again
    const isSubmitting = isLoading || currentPayment.waitingForPayment;

    const timeIntervals = 60 / Number(spaceData?.data.settings?.slot_division ?? 1);
    // Trainerstunden: this space only sells lessons, so a trainer has to be picked
    const trainerMode = Boolean(spaceData?.data.settings?.trainer_mode);
    const start = moment(slot.currentDate).startOf('day').add(slot.start, 'minutes');
    const end = start.clone().add(slot.width, 'minutes');
    const bookingRequestData = useMemo(
        () => ({ ...requestData, space_id: requestData?.space_id ?? slot.spaceId }),
        [requestData, slot.spaceId]
    );

    const handleSubmit = useCallback(
        (data: FieldValues) => {
            setIsLoading(true);
            let popupWindow = hasOnlinePayments ? preparePopup() : null;
            return api({
                url: bookingEndpoint ?? `space-manager/space/${slot.spaceId}/booking`,
                method: 'POST',
                data: { ...data, kiosk: kioskMode, ...bookingRequestData },
                // the method the booker picked stays on the form: a retry after a failed
                // payment should go out with the same choice, not silently lose it
                onBefore: () => setIsLoading(false),
                onError: () => handleError(popupWindow),
                onSuccess: (response) => {
                    const allocation = response?.data;
                    const payment = response?.payment ?? {};
                    const molliePaymentId = payment?.molliePaymentId ?? response?.molliePaymentId;
                    const redirectUrl = payment?.redirectUrl;

                    redirectToCheckout({
                        ...allocation,
                        ...payment,
                        molliePaymentId,
                        redirectUrl,
                        popupWindow,
                        callback: () => {
                            const undo = response?.undo as BookingUndoGrant | undefined;
                            setUndoGrant(undo ?? null);
                            onSuccess?.(allocation, undo);
                            setBookingData(allocation);
                        }
                    });
                }
            });
        },
        [slot, kioskMode, hasOnlinePayments, bookingEndpoint, bookingRequestData, onSuccess]
    );

    const handleNext = useCallback(() => formStepsRef.current?.nextStep(), []);
    const handleBack = useCallback(() => formStepsRef.current?.previousStep(), []);

    const { setUser, setToken, clearAuth, tempAuthenticated } = useTempAuth();
    // clear the temp auth session when the bookings assistant is closed
    useEffect(() => {
        clearAuth();
    }, []);

    const formStepsRef = useRef<FormStepsRef>(null);
    // holds InFormLogin's login trigger so the manual login can fire from the "Weiter" transition
    const authenticateRef = useRef<(() => boolean) | null>(null);
    // in-flight manual login, mirrored from InFormLogin to drive the "Weiter" button loading state
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    // set after a manual "Weiter" login so the step advances once auth state has settled
    const pendingAdvanceRef = useRef(false);

    // undo a kiosk login (temp-auth): clears the session and the prefilled identity so the
    // login options reappear; also used as the QR AppLogin refresh handler
    const handleClearAuth = useCallback(() => {
        clearAuth();
        formStepsRef.current?.helpers.setValue('user_email', '');
        formStepsRef.current?.helpers.setValue('user_name', '');
        setUserFound(false);
    }, [clearAuth]);

    // shared kiosk login handler: QR (AppLogin) and manual (InFormLogin) both land here
    // so a temp-auth session is established the same way regardless of login method.
    // advanceAfter is used by the manual login, which authenticates on "Weiter" and should
    // then move on — but via a deferred normal nextStep so required form fields still validate;
    // QR keeps its original auto-advance-only-when-no-fields behavior
    const handleAuthentication = useCallback(
        (token: string, authUser: { email?: string; name?: string }, advanceAfter = false) => {
            // login done: clear the "Weiter" loading flag even though InFormLogin unmounts on success
            setIsAuthenticating(false);
            setToken(token);
            setUser(authUser);
            formStepsRef.current?.helpers.setValue('user_email', authUser.email);
            formStepsRef.current?.helpers.setValue('user_name', authUser.name);
            setUserFound(true);
            if (advanceAfter) pendingAdvanceRef.current = true;
            else if (fields.length <= 0) formStepsRef.current?.nextStep();
        },
        [setToken, setUser, fields.length]
    );

    // manual "Weiter" login: once the temp-auth user is in place, run a normal nextStep so the
    // step's nextRequirements (incl. required booking form fields) are honored before advancing
    useEffect(() => {
        if (pendingAdvanceRef.current && user) {
            pendingAdvanceRef.current = false;
            formStepsRef.current?.nextStep();
        }
    }, [user]);
    const steps = useMemo(
        (): Step[] => [
            {
                title: t('bookingAssistant.steps.bookingDetails'),
                errorKeys: ['start', 'end', 'trainer_id'],
                nextRequirements: trainerMode ? ['trainer_id'] : [],
                content: (
                    <>
                        <div className="tw-flex tw-h-8 tw-cursor-not-allowed tw-select-none tw-items-center tw-justify-center tw-rounded-xl tw-bg-accents-0 tw-px-1 tw-text-sm tw-text-accents-7">
                            {spaceData?.data?.name ?? `#${String(slot.spaceId)}`}
                        </div>
                        <FormInput.TimeRange
                            endName="end"
                            startName="start"
                            withDate
                            disabled={kioskMode || !!fixedSlot}
                        />

                        {fixedSlot ? (
                            fixedSlot.trainer && (
                                <div className="tw-mt-4 tw-flex tw-h-8 tw-cursor-not-allowed tw-select-none tw-items-center tw-justify-center tw-rounded-xl tw-bg-accents-0 tw-px-1 tw-text-sm tw-text-accents-7">
                                    {t('allocation.form.trainerLabel')}: {fixedSlot.trainer.name}
                                </div>
                            )
                        ) : (
                            <>
                                <LengthInput
                                    spaceId={slot.spaceId}
                                    slotWidth={slot.width}
                                    timeIntervals={timeIntervals}
                                    clubSlug={clubSlug}
                                />
                                <BookingTrainerSelection
                                    spaceId={slot.spaceId}
                                    trainers={slot.trainers}
                                    trainerMode={trainerMode}
                                />
                            </>
                        )}
                    </>
                )
            },
            {
                title: t('bookingAssistant.steps.additionalInfo'),
                active: fields.length > 0 || !isAuthenticated || tempAuthenticated,
                nextRequirements: [
                    ...fields
                        .map(({ rules, name }) => (rules?.includes('required') ? `form_data.${name}` : null))
                        .filter(Boolean),
                    ...(!!user
                        ? []
                        : !userFound
                          ? ['user_email', 'user_name', 'sportision_legal_dsgvo', 'sportision_legal_agb']
                          : ['user_email', 'password'])
                ],
                // kiosk manual login happens on "Weiter": block the step and let a successful
                // login advance it, but only when an existing account still needs signing in
                nextCallback: () =>
                    kioskMode && !user && userFound ? !(authenticateRef.current?.() ?? false) : true,
                content: (
                    <>
                        {kioskMode &&
                            (tempAuthenticated ? (
                                // logged-in confirmation for either login method, with an undo action
                                <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-p-4">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2">
                                        <CompletedAnimation />
                                        <span className="tw-block tw-text-center tw-text-lg tw-text-accents-8">
                                            Angemeldet als {user?.name}
                                        </span>
                                    </div>
                                    <Button
                                        block
                                        type="button"
                                        color="secondary"
                                        className="tw-mt-4"
                                        onClick={handleClearAuth}
                                    >
                                        Anmeldung rückgängig machen
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <div className="tw-flex tw-justify-center">
                                        <AppLogin
                                            className="tw-max-w-[400px]"
                                            onRefresh={handleClearAuth}
                                            onAuthentication={handleAuthentication}
                                        />
                                    </div>
                                    <div className="tw-my-4 tw-flex tw-items-center tw-gap-4">
                                        <div className="tw-flex-1 tw-border-0 tw-border-t tw-border-solid tw-border-accents-2" />
                                        <span className="tw-italic tw-text-accents-6">{t('bookingAssistant.or')}</span>
                                        <div className="tw-flex-1 tw-border-0 tw-border-t tw-border-solid tw-border-accents-2" />
                                    </div>
                                </>
                            ))}
                        {!user && (
                            <InFormLogin
                                nameMapping={{
                                    name: 'user_name',
                                    email: 'user_email'
                                }}
                                onUserFound={setUserFound}
                                // kiosk manual login authenticates via the shared handler so the
                                // member list (privacy-gated) and booking run as the logged-in member;
                                // forceAdvance moves past this step once the "Weiter" login succeeds
                                onAuthenticate={
                                    kioskMode
                                        ? (token, authUser) => handleAuthentication(token, authUser, true)
                                        : undefined
                                }
                                // register the login trigger + loading state for the "Weiter" transition
                                registerAuthenticate={kioskMode ? (fn) => (authenticateRef.current = fn) : undefined}
                                onAuthenticatingChange={kioskMode ? setIsAuthenticating : undefined}
                            />
                        )}
                        <FormFields withoutLegal={!!user || userFound} namePrefix="form_data" fields={fields} />
                    </>
                )
            },
            {
                title: t('bookingAssistant.steps.addBookers'),
                active: !hideBookers,
                content: (
                    <>
                        <BookersFieldArray name="bookers" spaceId={slot.spaceId} clubSlug={clubSlug} />
                    </>
                )
            },
            {
                title: t('bookingAssistant.steps.customizeBooking'),
                active: !!clubSlug && addonData?.data?.length > 0,
                content: <AddonSelection clubSlug={clubSlug} />
            },
            {
                title: hasOnlinePayments
                    ? t('bookingAssistant.steps.payment')
                    : t('bookingAssistant.steps.summary'),
                renderWhenHidden: false,
                content: (
                    <>
                        <MollieConnectFeeWarning clubSlug={clubSlug} />
                        <VoucherFields className="tw-my-0" type="space_manager" showCards={false} />
                        {hasOnlinePayments && (
                            <>
                                {/* a mandate charges its own method, so it can't serve a forced one */}
                                {!forcedPaymentMethod && <MollieConnectMandates module="space_manager" />}
                                <PaymentMethodSelection
                                    className="tw-mt-4"
                                    spaceId={slot.spaceId}
                                    isLoading={isSubmitting}
                                    forcedMethod={forcedPaymentMethod}
                                />
                            </>
                        )}
                        {/* below the methods: a saved voucher is just another way to pay */}
                        <VoucherCards className="tw-mt-4" type="space_manager" />
                    </>
                )
            }
        ],
        [
            fields,
            kioskMode,
            isAuthenticated,
            slot,
            userFound,
            hideBookers,
            user,
            timeIntervals,
            addonData,
            hasOnlinePayments,
            forcedPaymentMethod,
            isSubmitting,
            summaryEndpoint,
            requestData,
            bookingRequestData,
            clubSlug,
            trainerMode,
            fixedSlot,
            handleAuthentication,
            handleClearAuth,
            tempAuthenticated,
            t
        ]
    );

    const schema = useMemo(
        () =>
            Joi.object({
                user_name: Joi.string()
                    .required()
                    .allow(...(userFound || !!user ? ['', null] : []))
                    .label(t('bookingAssistant.fieldLabels.name')),
                user_email: Joi.string()
                    .email({ tlds: false })
                    .required()
                    .allow(...(userFound || !!user ? ['', null] : []))
                    .label(t('bookingAssistant.fieldLabels.email')),
                password: Joi.string()
                    .optional()
                    .allow(...(userFound || !user ? ['', null] : []))
                    .label(t('bookingAssistant.fieldLabels.password')),
                trainer_email: Joi.string().optional().allow('', null).label(t('allocation.form.trainerLabel')),
                trainer_id: trainerMode
                    ? Joi.number().required().label(t('allocation.form.trainerLabel'))
                    : Joi.any().optional().allow('', null),
                ...(!user && !userFound
                    ? {
                          sportision_legal_agb: Joi.boolean()
                              .valid(true)
                              .required()
                              .label(t('bookingAssistant.fieldLabels.terms')),
                          sportision_legal_dsgvo: Joi.boolean()
                              .valid(true)
                              .required()
                              .label(t('bookingAssistant.fieldLabels.privacy'))
                      }
                    : {}),
                form_data: validationSchema
            }).unknown(),
        [userFound, user, validationSchema, trainerMode, t]
    );

    if (bookingData !== null)
        // the modal body is padding-free for the two-column layout, so the confirmation
        // has to bring its own
        return (
            <div className="tw-flex tw-min-h-full tw-flex-col tw-p-6 lg:tw-h-full">
                <Confirmation
                    fill
                    callback={() => callback?.(bookingData)}
                    // at the kiosk the undo bar takes over as the confirmation, so the modal must
                    // step aside on its own — the undo window is only seconds long
                    autoCloseMs={kioskMode && undoGrant ? CONFIRMATION_AUTO_CLOSE_MS : undefined}
                    title={requiresApproval ? t('bookingAssistant.requestConfirmation.title') : undefined}
                    message={requiresApproval ? t('bookingAssistant.requestConfirmation.message') : undefined}
                />
            </div>
        );

    return (
        <Loader isLoading={isLoadingSpace || isLoadingFields || isLoadingSettings || isLoadingAddons}>
            <Form
                onSubmit={handleSubmit}
                options={{ mode: 'onBlur' }}
                defaultValues={{
                    user_name: user?.name,
                    user_email: user?.email,
                    trainer_id: fixedSlot?.trainer?.id ?? null,
                    bookers: [],
                    start: start.toDate(),
                    end: end.toDate()
                }}
                validationSchema={schema}
                resetDefaultValuesOnRerender={false}
                setDefaultValuesOnRerender={false}
                // the form sits between the modal body and the two columns, so it has to pass
                // the pinned height down or the rail stops short of the bottom edge
                className="lg:tw-h-full"
            >
                <div className="tw-flex tw-min-h-full tw-flex-1 tw-flex-col lg:tw-h-full lg:tw-flex-row lg:tw-overflow-hidden">
                    <div className="tw-flex tw-flex-1 tw-flex-col lg:tw-min-h-0">
                        {stepState && (
                            <div className="tw-px-6 tw-pt-6">
                                <span className="tw-block tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-success">
                                    {t('bookingAssistant.stepCounter', {
                                        current: stepState.index + 1,
                                        total: stepState.total
                                    })}
                                </span>
                                <span className="tw-block tw-text-xl tw-font-semibold tw-text-accents-9">
                                    {stepState.title}
                                </span>
                                <Progress className="tw-mt-3" progress={(stepState.index + 1) / stepState.total} />
                            </div>
                        )}
                        {/* only the step content scrolls — counter and progress stay put */}
                        <div className="tw-flex tw-flex-1 tw-flex-col tw-px-6 tw-pb-6 lg:tw-min-h-0 lg:tw-overflow-y-auto">
                            <FormSteps
                                ref={formStepsRef}
                                hideHeader
                                ownControls
                                onStepChange={setStepState}
                                isLoading={isSubmitting || isAuthenticating}
                                steps={steps}
                            />
                        </div>
                    </div>
                    <BookingRail
                        slot={slot}
                        spaceName={spaceData?.data?.name}
                        kiosk={kioskMode}
                        summaryEndpoint={summaryEndpoint ?? `space-manager/space/${slot.spaceId}/summary`}
                        checkEndpoint={checkEndpoint}
                        requestData={bookingRequestData}
                        checkRules={showRuleChecker}
                        step={stepState}
                        isLoading={isSubmitting || isAuthenticating}
                        hasOnlinePayments={hasOnlinePayments}
                        submitTextOverride={submitText}
                        onRequiresApprovalChange={setRequiresApproval}
                        onPaymentRuleChange={handlePaymentRuleChange}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                </div>
            </Form>
        </Loader>
    );
};

export default BookingAssistant;
